import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Sparkles } from 'lucide-react';

import { StudioCreateModal } from '../StudioCreateModal';
import { buildStudioMaterialUrl } from '../../constants/studioMaterialRoutes';
import { buildCourseMaterialPath } from '../../constants/courseStudioRoutes';
import { buildLinkedSessionContext } from '../../types/linkedSessionContext';
import type { PlanSession, PlanUnit } from '../../types/coursePlan';
import { listResourcesForSession, type StudioResource } from '../../studioResources';

interface SessionLinkedMaterialsProps {
  courseId: string;
  subjectId: string;
  subjectTag: string;
  level: string;
  unit: PlanUnit;
  session: PlanSession;
}

function resourceEditHref(resource: StudioResource, subjectId: string, courseId: string): string {
  if (resource.kind === 'guide') {
    return buildStudioMaterialUrl({
      profile: 'learning_guide',
      subjectId,
      examId: resource.id,
      courseId,
    });
  }
  if (resource.kind === 'exam') {
    return buildStudioMaterialUrl({
      profile: 'assessment',
      subjectId,
      examId: resource.id,
      courseId,
    });
  }
  if (resource.kind === 'class') {
    return buildStudioMaterialUrl({
      profile: 'interactive_class',
      subjectId,
      examId: resource.id,
      courseId,
    });
  }
  const activityPath = `/studio/${resource.mode}?draft=${resource.id}&subject=${subjectId}`;
  return buildCourseMaterialPath(courseId, activityPath);
}

function resourceKindLabel(kind: StudioResource['kind']): string {
  switch (kind) {
    case 'guide':
      return 'Guía';
    case 'class':
      return 'Clase';
    case 'exam':
      return 'Evaluación';
    default:
      return 'Actividad';
  }
}

export function SessionLinkedMaterials({
  courseId,
  subjectId,
  subjectTag,
  level,
  unit,
  session,
}: SessionLinkedMaterialsProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const onFocus = () => setRefreshKey((key) => key + 1);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const linkedResources = useMemo(
    () => listResourcesForSession(courseId, unit.id, session.id),
    [courseId, unit.id, session.id, refreshKey],
  );

  const linkedSessionContext = useMemo(
    () =>
      buildLinkedSessionContext({
        courseId,
        unit,
        session,
        subjectTag,
        level,
      }),
    [courseId, unit, session, subjectTag, level],
  );

  return (
    <>
      <section className="border-t border-gray-100 pt-6">
        <h4 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
          Materiales de la sesión
        </h4>
        <div className="flex flex-wrap items-center gap-3">
          {linkedResources.map((resource) => (
            <Link
              key={resource.id}
              to={resourceEditHref(resource, subjectId, courseId)}
              className="inline-flex max-w-xs items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm transition hover:border-gray-300 hover:shadow"
              title={`${resourceKindLabel(resource.kind)} · ${resource.title}`}
              onClick={(event) => event.stopPropagation()}
            >
              <FileText className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
              <span className="truncate font-medium">{resource.title}</span>
            </Link>
          ))}

          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 shadow-sm transition hover:border-gray-400 hover:bg-gray-50"
            onClick={(event) => {
              event.stopPropagation();
              setCreateOpen(true);
            }}
          >
            <Sparkles className="h-4 w-4 text-gray-600" aria-hidden />
            + Crear material con IA
          </button>
        </div>
      </section>

      <StudioCreateModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        subjectId={subjectId}
        linkedSessionContext={linkedSessionContext}
      />
    </>
  );
}
